import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { getAuth } from 'firebase/auth';
import { Observable, of } from 'rxjs';
import { catchError, map, take, timeout } from 'rxjs/operators';

const AUTH_READINESS_MS = 2000;

@Injectable({
  providedIn: 'root',
})
export class PublicOnlyGuard implements CanActivate {
  constructor(
    private readonly auth: AngularFireAuth,
    private readonly router: Router
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    return this.auth.authState.pipe(
      take(1),
      // On slow auth init, don't fall back to `null` (which would let an authed
      // member reach login/signup). Re-resolve from the synchronous Firebase user.
      timeout({
        first: AUTH_READINESS_MS,
        with: () => of(this.getSynchronousUser()),
      }),
      map((user) => this.resolveAccess(user)),
      // On unexpected errors, fail safe using the synchronous user instead of
      // blindly allowing access to public-only routes.
      catchError(() => of(this.resolveAccess(this.getSynchronousUser())))
    );
  }

  private getSynchronousUser(): { isAnonymous: boolean } | null {
    try {
      return getAuth().currentUser;
    } catch {
      return null;
    }
  }

  private resolveAccess(
    user: { isAnonymous: boolean } | null
  ): boolean | UrlTree {
    if (!user || user.isAnonymous) {
      return true;
    }
    return this.router.createUrlTree(['mainTabs', 'tabs', 'home']);
  }
}
